/**
 * ParagraphIndent
 * Adds Tab / Shift+Tab indentation to paragraphs and headings in the Help
 * article editor.
 *
 * Indentation is stored as a bounded integer level in `data-indent` rather than
 * an inline `style`, so the backend sanitizer can keep rejecting `style`
 * outright (it is an XSS-adjacent attribute) while still round-tripping this
 * one value. The level maps to a CSS margin at render time; see
 * ArticleBody.module.css and RichTextEditor.module.css.
 *
 * Tab is deliberately NOT bound inside lists: the list extensions already use
 * it to nest items, and that binding must keep winning.
 */
import { Extension, type CommandProps, type RawCommands } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export const INDENT_ATTRIBUTE = 'data-indent';
export const MIN_INDENT_LEVEL = 0;
export const MAX_INDENT_LEVEL = 4;

/** Node types that accept indentation. */
const INDENTABLE_TYPES = ['paragraph', 'heading'];

/** Types whose own Tab handling must take precedence over indentation. */
const LIST_TYPES = ['bulletList', 'orderedList', 'listItem'];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    paragraphIndent: {
      /** Increases the indent level of the selected blocks. */
      indentBlock: () => ReturnType;
      /** Decreases the indent level of the selected blocks. */
      outdentBlock: () => ReturnType;
    };
  }
}

/**
 * Coerces an arbitrary parsed value to a valid level, or null when absent.
 * Guards against hand-edited or pasted markup carrying junk (`data-indent="99"`,
 * `data-indent="abc"`) into the document.
 */
export function normalizeIndentLevel(value: unknown): number | null {
  const level = Number(value);
  if (!Number.isInteger(level) || level <= MIN_INDENT_LEVEL) return null;
  return Math.min(level, MAX_INDENT_LEVEL);
}

export const ParagraphIndent = Extension.create({
  name: 'paragraphIndent',

  // Above the list extensions, which also bind Tab/Shift-Tab. The inList()
  // guards below hand both keys back whenever the selection is actually inside
  // a list, so list nesting keeps working.
  priority: 200,

  addGlobalAttributes() {
    return [
      {
        types: INDENTABLE_TYPES,
        attributes: {
          indent: {
            default: null,
            parseHTML: (element) => normalizeIndentLevel(element.getAttribute(INDENT_ATTRIBUTE)),
            renderHTML: (attributes) => {
              const level = normalizeIndentLevel(attributes.indent);
              // Omit the attribute entirely at level 0 so unindented content
              // stays byte-identical to what it was before this extension.
              return level ? { [INDENT_ATTRIBUTE]: String(level) } : {};
            }
          }
        }
      }
    ];
  },

  addCommands() {
    /** Shifts the indent of every indentable block touched by the selection. */
    const shiftIndent =
      (delta: number) =>
      ({ state, tr, dispatch }: CommandProps): boolean => {
        const { from, to } = state.selection;
        let changed = false;

        state.doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number) => {
          if (!INDENTABLE_TYPES.includes(node.type.name)) return;

          const current = normalizeIndentLevel(node.attrs.indent) ?? MIN_INDENT_LEVEL;
          const next = Math.min(Math.max(current + delta, MIN_INDENT_LEVEL), MAX_INDENT_LEVEL);
          if (next === current) return;

          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next || null });
          changed = true;
        });

        if (changed && dispatch) dispatch(tr);
        return changed;
      };

    return {
      indentBlock: () => shiftIndent(1),
      outdentBlock: () => shiftIndent(-1)
    } as Partial<RawCommands>;
  },

  addKeyboardShortcuts() {
    /**
     * Lists own Tab for nesting. Returning false here lets the list keymap
     * handle the event instead of swallowing it.
     */
    const inList = (): boolean => LIST_TYPES.some((type) => this.editor.isActive(type));

    // Both keys are consumed unconditionally once the selection is outside a
    // list, including at the min/max levels where the command itself is a
    // no-op. Returning false there would let the browser's default Tab move
    // focus out of the editor mid-edit - and the following keystroke would then
    // land on whatever received focus rather than the article body.
    return {
      Tab: () => {
        if (inList()) return false;
        this.editor.commands.indentBlock();
        return true;
      },
      'Shift-Tab': () => {
        if (inList()) return false;
        this.editor.commands.outdentBlock();
        return true;
      }
    };
  }
});

export default ParagraphIndent;
